import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button as ReactButton } from '@a-ui/react';
import BackLink from '../../components/BackLink';
import CheckGroup from '../../components/CheckGroup';
import Form from '../../components/Form';
import RadioGroup from '../../components/RadioGroup';
import { translate } from '../../i18n';
import { ITranslations } from '../../i18n/config';
import { useClickOutside } from './hooks';

interface NewsletterFilterModalProps {
  categories: string[];
  setVisible: (visible: boolean) => void;
  onCancel: () => void;
  cancelLabel: ITranslations;
  showBackLink?: boolean;
}

const NewsletterFilterModal: React.FC<NewsletterFilterModalProps> = ({
  categories,
  setVisible,
  cancelLabel,
  onCancel,
  showBackLink,
}) => {
  const modalRef = React.useRef<HTMLDivElement>(null);
  const { search } = useLocation();
  const params = React.useMemo(() => new URLSearchParams(search), [search]);
  const history = useHistory();

  const filterOptions = React.useMemo(() => {
    return categories.map((category) => ({
      label: category,
      value: category,
    }));
  }, [categories]);

  const sortOptions = [
    { label: translate('newsletters.sort.alphabetical.label'), value: 'name' },
    { label: translate('newsletters.sort.subscriptions_desc.label'), value: 'subscription:desc' },
    { label: translate('newsletters.sort.subscriptions_asc.label'), value: 'subscription:asc' },
  ];

  const initialValues = React.useMemo(() => {
    return {
      categories: params.has('categories[]') ? params.getAll('categories[]') : categories,
      sort: params.has('sort') ? params.get('sort') : 'name',
    };
  }, [params, categories]);

  const handleSubmit = (values: {
    categories: string[];
    sort: 'name' | 'subscription:desc' | 'subscription:asc';
  }) => {
    if (values.categories.sort().toString() !== params.getAll('categories[]').toString()) {
      params.set('page', '1');
      params.delete('categories[]');
      values.categories.forEach((category: string) => {
        params.append('categories[]', category);
      });
    }

    if (values.sort !== params.get('sort')) {
      params.set('page', '1');
      params.set('sort', values.sort);
    }

    setVisible(false);
    history.push({ search: params.toString() });
  };

  useClickOutside<HTMLDivElement>(modalRef, () => setVisible(false));

  return (
    <div className="ckp-o-newsletter-filter__modal" ref={modalRef}>
      <div className="ckp-o-newsletter-filter__modal__content">
        <Form
          onSubmit={handleSubmit}
          initialValues={initialValues}
          classNames="ckp-o-newsletter-filter__form"
        >
          <div className="ckp-o-newsletter-filter__form__content">
            {showBackLink && (
              <BackLink backAction={() => setVisible(false)} backLabel="newsletters.title" />
            )}

            {categories.length > 1 && (
              <CheckGroup
                name="categories"
                label={translate('newsletters.filter.label')}
                options={filterOptions}
              />
            )}

            <RadioGroup
              name="sort"
              label={translate('newsletters.sort.label')}
              options={sortOptions}
            />
          </div>

          <div className="ckp-o-newsletter-filter__form__footer">
            <ReactButton htmlType="submit">{translate('newsletters.filter.submit')}</ReactButton>
            <ReactButton emphasis="low" onClick={onCancel}>
              {translate(cancelLabel)}
            </ReactButton>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default NewsletterFilterModal;
